import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';
import { useDispatch, useSelector } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { DangerAlert, InfoAlert, WaitingAlert, Table, BtnBar } from '@tmapy/style-guide';

import { CheckFilter } from './CheckFilter';
import { CheckListItem } from './CheckListItem';
import { DocExport } from './DocExport';
import { checkListMessages } from './docsMessages';
import { DOC_STATES_MANAGER_IMPORTS, CHECK_FLOWS } from '../constants';
import { QUERY_STATE } from '../../constants';

import { useDocsListResource } from '../../openapi/operations/docsList';
import { DocCheckStateEnum } from '../../openapi/types/DocCheckStateEnum';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { FilterBtn } from '../../app/components/FilterBtn';

export type CheckListProps = {
  workType?: 'PREPARING';
};

export const CheckList: React.FC<CheckListProps> = ({ workType }) => {
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const navigationType = useNavigationType();
  const filters = useSelector((state) => state.filter.checkList);
  const { manager } = useSelector((state) => state.app.config);

  const query: Record<string, string | number | boolean | string[]> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }
  switch (workType) {
    case 'PREPARING': {
      if (!query.state__in) {
        query.state__in = Object.keys(DOC_STATES_MANAGER_IMPORTS);
      }
      break;
    }
    default: {
      query.flow__in = CHECK_FLOWS;
    }
  }
  // ABORTED filtruje BE pres check_not_completed, ktery zahrnuje i INTERRUPTED a nedorucenou R17
  const latestCheckStates = query.latest_check_state__in;
  if (Array.isArray(latestCheckStates) && latestCheckStates.includes(DocCheckStateEnum.ABORTED)) {
    const otherStates = latestCheckStates.filter(
      (state) => state !== DocCheckStateEnum.ABORTED && state !== DocCheckStateEnum.INTERRUPTED,
    );
    if (otherStates.length) {
      query.latest_check_state__in = otherStates;
    } else {
      delete query.latest_check_state__in;
    }
    query.check_not_completed = true;
  }

  const { queryState, data, fetchMore } = useDocsListResource(query, navigationType !== 'PUSH');

  const checkList = data?.results ?? [];
  const checkCount = data?.count;

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  function handleNextCheckBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(checkListMessages.checkListFiltrLabel)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='checklist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...checkListMessages.checkListPageTitle} />
            </PageTitle>
            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
              {manager?.modules?.docExport && (
                <DocExport
                  query={query}
                  exportType='CHECK'
                  label={formatMessage(checkListMessages.checkListExportCsvLabel)}
                  fileName={'seznam_kontrol.csv'}
                />
              )}
            </BtnBar>
          </header>
          {queryState === QUERY_STATE.WAITING && checkList.length < 1 && (
            <WaitingAlert>
              <FormattedMessage {...checkListMessages.checkListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && checkList.length < 1 && (
            <DangerAlert>
              <FormattedMessage {...checkListMessages.checkListStateFail} />
            </DangerAlert>
          )}
          {queryState === QUERY_STATE.SUCCESS && checkList.length < 1 && (
            <>
              {isFiltered ? (
                <InfoAlert>
                  <FormattedMessage {...checkListMessages.checkListStateFilteredEmpty} />
                </InfoAlert>
              ) : (
                <InfoAlert>
                  <FormattedMessage {...checkListMessages.checkListStateEmpty} />
                </InfoAlert>
              )}
            </>
          )}
          {checkList.length > 0 && (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...checkListMessages.checkListColumnNumber} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnState} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnName} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnCheckSource} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnDmvsIdPodani} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnCreated} />
                      </th>
                      <th>
                        <FormattedMessage {...checkListMessages.checkListColumnAuthor} />
                        <br />
                        <span className='dmx-u-c-cta-gray'>
                          <FormattedMessage {...checkListMessages.checkListColumnGeodesist} />
                        </span>
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {checkList.map((checkItem) => (
                      <CheckListItem key={checkItem.id} workType={workType} data={checkItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={checkList.length}
                total={checkCount!}
                queryState={queryState}
                onNextBtnClick={handleNextCheckBtnClick}
              />
            </div>
          )}
        </article>
      </MainContent>
      <CheckFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
